import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "./Projects.css";
import ProjectsList from "./ProjectsList/ProjectsList";
import ProjectView from "./ProjectView/ProjectView";
import ProjectViewer from "./ProjectViewer/ProjectViewer";
import { useProject } from "../../contexts/ProjectContext";
import {
  preloadVirtualTourMapping,
  preloadAllVirtualTours,
} from "../../utils/virtualTourMapping";

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get the project ID from URL params

  // Check if we're on a 3D view route
  const is3DView = location.pathname.endsWith("/view");
  const [currentView, setCurrentView] = useState(() =>
    id ? (is3DView ? "viewer" : "project") : "list",
  ); // 'list', 'project', 'viewer'
  const [user, setUser] = useState(null);
  const [projectLoadTimeout, setProjectLoadTimeout] = useState(null);
  const hasLoadedProjectsRef = useRef(false); // Track if we've loaded projects to prevent duplicate loads

  useEffect(() => {
    const normalizedPathname = location.pathname.replace(/\/{2,}/g, "/");
    if (normalizedPathname !== location.pathname) {
      console.warn(
        "Normalizing malformed projects path:",
        location.pathname,
        "->",
        normalizedPathname,
      );
      navigate(
        {
          pathname: normalizedPathname,
          search: location.search,
          hash: location.hash,
        },
        { replace: true },
      );
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  // Debug component lifecycle
  console.log("🔍 Projects Component - MOUNTED/RENDERED:", {
    pathname: location.pathname,
    id,
    timestamp: new Date().toISOString(),
  });

  // Debug URL parameters immediately
  console.log("🔍 Projects Component - URL Debug:", {
    "location.pathname": location.pathname,
    "location.search": location.search,
    "location.hash": location.hash,
    "id from useParams": id,
    "id type": typeof id,
    "id === undefined": id === undefined,
    "id === null": id === null,
    'id === ""': id === "",
    "id value": id,
  });

  // Debug navigation calls
  const originalNavigate = navigate;
  const debugNavigate = (...args) => {
    console.log("🔍 Projects - navigate called:", {
      args,
      currentPath: location.pathname,
      id,
    });
    return originalNavigate(...args);
  };

  const safeProjectNavigate = (...args) => {
    if (typeof args[0] === "string" && args[0] === "/projects" && id) {
      console.warn(
        "Blocking automatic redirect to /projects while resolving explicit project route:",
        {
          requestedProjectId: id,
          currentPath: location.pathname,
        },
      );
      return;
    }
    return debugNavigate(...args);
  };

  // Use project context
  const {
    projects,
    selectedProject,
    projectData,
    loading,
    error,
    loadProjects,
    areProjectsLoaded,
    setSelectedProject,
    clearError,
    clearSelectedProject,
  } = useProject();

  useEffect(() => {
    // Load user info from session storage
    const userToken = sessionStorage.getItem("token");
    if (userToken) {
      try {
        const userData = JSON.parse(userToken);
        setUser(userData);
      } catch (error) {
        console.error("Error parsing user token:", error);
      }
    }

    // Preload virtual tour mapping data in the background
    preloadVirtualTourMapping().catch((error) => {
      console.error("Error preloading virtual tour mapping:", error);
    });

    // Preload all virtual tours data in the background
    preloadAllVirtualTours().catch((error) => {
      console.error("Error preloading virtual tours:", error);
    });

    // Load all projects - always refresh when component mounts to ensure filtered list
    // This ensures we get the latest filtered projects (user + UnrealProjects secret filter)
    if (!hasLoadedProjectsRef.current) {
      console.log("🔍 Loading/refreshing projects to ensure filtered list...");
      loadProjects(true); // Force refresh to get filtered projects from API
      hasLoadedProjectsRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle URL changes to load specific projects
  useEffect(() => {
    console.log("🔍 URL change detected:", {
      pathname: location.pathname,
      projectId: id,
      projectIdType: typeof id,
      projectsLength: projects?.length || 0,
      projects: Array.isArray(projects)
        ? projects.map((p) => ({ id: p.Id, name: p.Name }))
        : [],
      loading: loading,
      areProjectsLoaded: areProjectsLoaded(),
    });

    // Additional debugging for projects array
    console.log("🔍 Projects Array Debug:", {
      projects: projects,
      "projects type": typeof projects,
      "projects is array": Array.isArray(projects),
      "projects length": projects?.length,
      "projects === null": projects === null,
      "projects === undefined": projects === undefined,
    });

    // Show actual project IDs
    if (projects && projects.length > 0) {
      console.log(
        "🔍 Available Project IDs:",
        projects.map((p) => ({ id: p.Id, name: p.Name, idType: typeof p.Id })),
      );
    }

    // Additional debugging for URL parameter
    console.log("🔍 URL Parameter Debug:", {
      "id from useParams": id,
      "id type": typeof id,
      "id === undefined": id === undefined,
      "id === null": id === null,
      'id === ""': id === "",
      "id value": id,
    });

    // Additional debugging for router state
    console.log("🔍 Router debugging:", {
      "location.pathname": location.pathname,
      "location.search": location.search,
      "location.hash": location.hash,
      "id from useParams": id,
      "id type": typeof id,
      "id === undefined": id === undefined,
      "id === null": id === null,
    });

    // Check if URL contains a project ID (e.g., /projects/2405)
    if (id) {
      // Convert projectId to number for comparison
      const numericProjectId = parseInt(id, 10);

      console.log("🔍 PROJECT ID DETECTED:", {
        id: id,
        numericProjectId: numericProjectId,
        is3DView: is3DView,
        projectsLength: projects?.length || 0,
        loading: loading,
        areProjectsLoaded: areProjectsLoaded(),
      });

      // Find the project with matching ID
      if (projects && projects.length > 0) {
        console.log(
          "Searching for project ID:",
          numericProjectId,
          "in projects:",
          projects.map((p) => ({
            id: p.Id,
            name: p.Name,
            idType: typeof p.Id,
          })),
        );
        console.log(
          "Available project IDs:",
          projects.map((p) => p.Id),
        );
        console.log(
          "Looking for ID type:",
          typeof numericProjectId,
          "Value:",
          numericProjectId,
        );
        console.log(
          "Project ID types in array:",
          projects.map((p) => ({ id: p.Id, type: typeof p.Id })),
        );

        // Try both string and number comparison
        const project = projects.find(
          (p) =>
            p.Id === numericProjectId ||
            p.Id === id ||
            p.Id === String(numericProjectId),
        );
        console.log("Found project:", project);
        if (project) {
          // Project found, set it as selected and show appropriate view
          console.log(
            "Setting selected project:",
            project.Name,
            "is3DView:",
            is3DView,
          );
          setSelectedProject(project);
          setCurrentView(is3DView ? "viewer" : "project");

          // Clear any existing timeout since we found the project
          if (projectLoadTimeout) {
            clearTimeout(projectLoadTimeout);
            setProjectLoadTimeout(null);
          }
        } else {
          // Project not found in loaded projects
          // Only redirect if we're sure projects have fully loaded
          // For direct navigation (like /projects/2259/view), we need to be more careful
          if (areProjectsLoaded() && !loading) {
            console.warn(
              `🔍 REDIRECT: Project with ID ${numericProjectId} not found. Available projects:`,
              projects.map((p) => p.Id),
            );
            console.warn(`🔍 REDIRECT: Navigating to /projects`);
            safeProjectNavigate("/projects");
          } else {
            // Projects might still be loading, wait a bit more
            console.log(
              `🔍 Project ${numericProjectId} not found yet, but projects might still be loading. Waiting...`,
            );
            if (!projectLoadTimeout) {
              const timeout = setTimeout(() => {
                // After waiting, check again if project exists
                const project = projects.find(
                  (p) =>
                    p.Id === numericProjectId ||
                    p.Id === id ||
                    p.Id === String(numericProjectId),
                );
                if (!project && areProjectsLoaded() && !loading) {
                  console.warn(
                    `🔍 REDIRECT: Project with ID ${numericProjectId} still not found after waiting. Redirecting to /projects`,
                  );
                  safeProjectNavigate("/projects");
                }
              }, 5000); // Wait 5 seconds for projects to load
              setProjectLoadTimeout(timeout);
            }
          }
        }
      } else if (projects && projects.length === 0 && !loading) {
        // Projects loaded but empty array - project doesn't exist
        console.warn(
          `Project with ID ${numericProjectId} not found - no projects available`,
        );
        // Don't redirect immediately, wait for projects to load
        console.log("Waiting for projects to load before redirecting...");
      } else {
        console.log(
          "Projects array is empty or not loaded yet, waiting for projects to load...",
          {
            projectsLength: projects?.length || 0,
            loading: loading,
            areProjectsLoaded: areProjectsLoaded(),
          },
        );

        // Set a timeout to prevent infinite waiting
        // Use longer timeout for /view routes (opened in new tabs) to allow auth/projects to load
        const timeoutDuration = is3DView ? 20000 : 10000; // 20 seconds for /view, 10 for regular
        if (!projectLoadTimeout) {
          console.log(
            `Setting timeout for project load (${timeoutDuration}ms for ${is3DView ? "view" : "regular"} route)...`,
          );
          const timeout = setTimeout(() => {
            console.warn(
              "Project load timeout reached, redirecting to projects list",
            );
            safeProjectNavigate("/projects");
          }, timeoutDuration);
          setProjectLoadTimeout(timeout);
        }
      }
    } else {
      // URL is just /projects, show projects list
      console.log("Showing projects list");
      setCurrentView("list");
      clearSelectedProject();

      // Force refresh projects when navigating to /projects to ensure filtered list
      // This ensures we get projects filtered by user + UnrealProjects secret
      // Only refresh if projects are already loaded and we've completed initial load
      // This prevents duplicate loads on initial mount
      if (hasLoadedProjectsRef.current && areProjectsLoaded()) {
        console.log(
          "🔄 Refreshing projects list to ensure filter is applied...",
        );
        loadProjects(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.pathname]); // Only depend on URL changes, not on loadProjects

  // Handle project loading when projects array changes
  useEffect(() => {
    if (id && projects && projects.length > 0) {
      const numericProjectId = parseInt(id, 10);
      const project = projects.find(
        (p) =>
          p.Id === numericProjectId ||
          p.Id === id ||
          p.Id === String(numericProjectId),
      );

      if (project) {
        console.log(
          "🔍 PROJECT FOUND AFTER LOADING:",
          project.Name,
          "is3DView:",
          is3DView,
        );
        setSelectedProject(project);
        setCurrentView(is3DView ? "viewer" : "project");

        // Clear any existing timeout since we found the project
        if (projectLoadTimeout) {
          clearTimeout(projectLoadTimeout);
          setProjectLoadTimeout(null);
        }
      } else {
        // Only redirect if we're absolutely sure projects have fully loaded
        // This prevents premature redirects on direct navigation
        if (areProjectsLoaded() && !loading) {
          console.warn(
            `🔍 PROJECT NOT FOUND AFTER LOADING: ${numericProjectId}. Available:`,
            projects.map((p) => p.Id),
          );
          console.warn(
            `🔍 Projects fully loaded and project not found, redirecting to /projects`,
          );
          safeProjectNavigate("/projects");
        } else {
          console.log(
            `🔍 Project ${numericProjectId} not found yet, but projects might still be loading. Waiting...`,
          );
        }
      }
    }
  }, [
    projects,
    id,
    setSelectedProject,
    navigate,
    projectLoadTimeout,
    is3DView,
    loading,
    areProjectsLoaded,
  ]);

  // Cleanup timeout on unmount or URL change
  useEffect(() => {
    return () => {
      console.log("🔍 Projects Component - UNMOUNTING:", {
        pathname: location.pathname,
        id,
        timestamp: new Date().toISOString(),
      });
      if (projectLoadTimeout) {
        clearTimeout(projectLoadTimeout);
        setProjectLoadTimeout(null);
      }
    };
  }, [projectLoadTimeout, location.pathname, id]);

  const handleProjectSelect = (project) => {
    // Navigate to the project-specific URL
    debugNavigate(`/projects/${project.Id}`);
  };

  const handleViewProject = (project) => {
    console.log("🔍 Navigating to 3D view for project:", project.Id);
    debugNavigate(`/projects/${project.Id}/view`);
  };

  // Define custom redirect paths for specific projects
  const getCustomRedirectPaths = () => {
    // Example: You can customize redirect paths for specific project IDs
    // Return an object where keys are project IDs and values are the redirect URLs
    return {
      // Example redirects (replace with your actual project IDs and desired paths):
      // 123: '/custom-path-for-project-123',
      // 456: '/another-custom-path',
      // 789: 'https://external-site.com'
    };
  };

  const handleBackToList = () => {
    console.log("🔍 Navigating back to list, forcing project refresh");
    navigate("/projects");
    setCurrentView("list");
    clearSelectedProject();
    // Force refresh when going back to list to ensure filter is applied
    loadProjects(true);
  };

  const handleBackToProject = () => {
    if (selectedProject) {
      console.log(
        "🔍 Navigating back to project view for:",
        selectedProject.Id,
      );
      debugNavigate(`/projects/${selectedProject.Id}`);
    } else {
      navigate("/projects");
    }
  };

  const renderCurrentView = () => {
    const isAwaitingProjectResolution =
      Boolean(id) &&
      (!selectedProject || String(selectedProject?.Id ?? "") !== String(id)) &&
      (loading || !areProjectsLoaded());

    if (isAwaitingProjectResolution) {
      return (
        <div className="project-view-container">
          <div className="project-view-loading">
            <div className="project-view-loading-spinner" aria-hidden="true" />
            <h2>Loading project...</h2>
          </div>
        </div>
      );
    }

    if (id && !selectedProject && !loading && areProjectsLoaded()) {
      return (
        <div className="project-view-container">
          <div className="project-view-loading">
            <h2>Project not found</h2>
            <p>
              The requested project could not be resolved from the current
              projects list.
            </p>
            <button
              type="button"
              className="back-btn"
              onClick={handleBackToList}
            >
              Back to Projects
            </button>
          </div>
        </div>
      );
    }

    switch (currentView) {
      case "project":
        return (
          <ProjectView
            key={selectedProject?.Id ?? id ?? "no-project"}
            project={selectedProject}
            projectData={projectData}
            onBack={handleBackToList}
            onViewProject={handleViewProject}
          />
        );
      case "viewer":
        return (
          <ProjectViewer
            key={selectedProject?.Id ?? id ?? "no-project"}
            project={selectedProject}
            onBack={handleBackToProject}
          />
        );
      default:
        return (
          <ProjectsList
            projects={projects}
            loading={loading}
            onProjectSelect={handleProjectSelect}
            onViewProject={handleViewProject}
            onRefresh={() => loadProjects(true)}
            user={user}
            customRedirectPaths={getCustomRedirectPaths()}
          />
        );
    }
  };

  return <div className="projects-container">{renderCurrentView()}</div>;
};

export default Projects;
